import React, { useEffect, useState } from "react";
import { UnitFormatsProvider } from "@strava/units/react";
import { Map, RoadSurfaceType, getPrivacyZonesFromIndices } from "@strava/feature-global-wasm-mre";
import type { ActivitySection } from "@strava/feature-global-wasm-mre";

import I18n from "../../../../../utils/I18n";

import StravaPackageContextProviders from "../../../../StravaPackageContextProviders";

import styles from "./SelectHiddenMap.scss";

interface SelectHiddenMapProps {
  activityId: number;
  activityLatLng: [number, number][];
  hideEntireMap: boolean;
  startIndex: number;
  endIndex: number;
  analyticsCategory: string; // Analytics category for track events (ADP subsection / map instance)
}

export const SelectHiddenMap = ({
  activityId,
  activityLatLng,
  hideEntireMap,
  startIndex,
  endIndex,
  analyticsCategory
}: SelectHiddenMapProps) => {
  const [activitySections, setActivitySections] = useState<ActivitySection[]>([]);

  // Determine the activity sections to display on the map
  useEffect(() => {
    if (hideEntireMap) {
      // If the entire map is to be hidden, set a single private section covering the whole activity
      setActivitySections([
        {
          numPolylineEdges: BigInt(activityLatLng?.length - 1), // -1 to account for the number of edges being one less than number of points
          privateSection: true,
          surfaceType: RoadSurfaceType.PAVED
        }
      ]);
      return;
    }

    // Otherwise, set the activity sections based on the start and end indices
    setActivitySections(
      getPrivacyZonesFromIndices(startIndex, activityLatLng?.length - endIndex, activityLatLng)
    );

  }, [startIndex, endIndex, activityLatLng, hideEntireMap]);

  return (
    <div className={styles.map}>
      <StravaPackageContextProviders>
        <UnitFormatsProvider
          locale={I18n.language()}
          unitSystem={I18n.measurementUnits()}
        >
          <Map
            polyline={activityLatLng}
            showPOIs={true}
            page={process.env.ACTIVITY_DETAILS_EDIT_MAP_VISIBILITY_ANALYTICS_PAGE || ''}
            category={analyticsCategory || ''}
            surfaceIdentifier={process.env.ACTIVITY_DETAILS_SURFACE_IDENTIFIER}
          >
            <Map.UIControls />
            <Map.Activity
              id={activityId.toString()}
              polyline={activityLatLng}
              activitySections={activitySections}
            />
          </Map>
        </UnitFormatsProvider>
      </StravaPackageContextProviders>
    </div>
  );
};

export default SelectHiddenMap;
